/**
 * Hand-drawn style SVG sketches of a human body performing workouts and stretches.
 * Rendered inline (no assets, no API calls). A subtle turbulence filter gives each
 * figure a sketched-by-hand wobble. Match an exercise name to the closest sketch
 * via keyword lookup.
 */

// Turbulence + displacement filter for a hand-drawn wobble on strokes/fills
const roughFilter = (
  <defs>
    <filter id="rough" x="-5%" y="-5%" width="110%" height="110%">
      <feTurbulence type="fractalNoise" baseFrequency="0.02" numOctaves="2" seed="5" />
      <feDisplacementMap in="SourceGraphic" scale="1.2" />
    </filter>
  </defs>
);

// Silhouette palette — parchment fill, ink outline
const INK = '#1A2421';
const SKIN = '#F5E9D8';
const ACCENT = '#4A7C59';

const bodyFill = { fill: SKIN, stroke: INK, strokeWidth: 1.8, strokeLinejoin: 'round', strokeLinecap: 'round' };
const detailLine = { stroke: INK, strokeWidth: 1.2, fill: 'none', strokeLinecap: 'round' };
const equipment = { fill: ACCENT, stroke: INK, strokeWidth: 1.4 };
const ground = { stroke: '#7E9979', strokeWidth: 1.4, fill: 'none', strokeDasharray: '3 4', strokeLinecap: 'round' };

// Each sketch is a 100x100 viewBox. Bodies use closed filled paths so they
// read as "a person" not a stick figure. Small details (hair line, eye)
// keep it human without leaning cartoony.

// --- SQUAT (side view, hips back, arms forward) ---------------------------
const Squat = () => (
  <svg viewBox="0 0 100 100" filter="url(#rough)">
    {roughFilter}
    {/* head */}
    <path d="M50 8 q7 0 8 8 q0 7 -8 8 q-8 -1 -8 -8 q1 -8 8 -8Z" {...bodyFill} />
    {/* torso, hips back, chest forward */}
    <path d="M46 24 q-4 8 -4 20 q0 8 6 12 q10 4 16 -2 q4 -4 4 -12 q0 -14 -6 -18 q-6 -2 -16 0Z" {...bodyFill} />
    {/* front arm reaching forward */}
    <path d="M52 30 q10 4 20 8 q3 2 2 5 q-2 3 -6 2 q-10 -3 -18 -6Z" {...bodyFill} />
    {/* back thigh horizontal */}
    <path d="M48 52 q14 4 24 6 q4 1 4 5 q-1 4 -6 4 q-14 -1 -24 -5Z" {...bodyFill} />
    {/* front thigh */}
    <path d="M56 56 q10 6 12 16 q0 4 -4 5 q-5 0 -6 -4 q-3 -8 -8 -14Z" {...bodyFill} />
    {/* front calf/foot */}
    <path d="M62 76 q4 2 12 4 q3 1 3 3 q-1 2 -3 2 l-14 -1 q-3 0 -3 -4Z" {...bodyFill} />
    {/* back foot */}
    <path d="M72 60 q6 2 10 6 q1 2 -1 3 q-5 1 -12 -4Z" {...bodyFill} />
    {/* hair line detail */}
    <path d="M43 12 q5 -6 14 -4" {...detailLine} />
    <path d="M14 84 h72" {...ground} />
  </svg>
);

// --- PUSH-UP (side plank on hands) ----------------------------------------
const Pushup = () => (
  <svg viewBox="0 0 100 100" filter="url(#rough)">
    {roughFilter}
    {/* head */}
    <path d="M14 44 q7 -1 8 6 q0 6 -8 6 q-6 -1 -6 -6 q0 -6 6 -6Z" {...bodyFill} />
    {/* torso long */}
    <path d="M20 46 q30 -2 54 2 q3 1 3 4 q0 3 -3 4 q-30 4 -54 2Z" {...bodyFill} />
    {/* front arm bent */}
    <path d="M28 54 q1 8 3 16 q1 3 4 3 q3 -1 2 -4 q-2 -8 -3 -15Z" {...bodyFill} />
    {/* rear leg */}
    <path d="M74 50 q10 4 16 12 q2 3 -1 4 q-4 1 -8 -2 q-6 -4 -10 -8Z" {...bodyFill} />
    {/* front leg */}
    <path d="M52 52 q6 12 8 20 q0 3 -3 3 q-3 0 -5 -3 q-4 -8 -6 -18Z" {...bodyFill} />
    {/* hand under head */}
    <path d="M30 70 q6 1 8 4 q0 2 -2 2 h-7 q-2 -1 -1 -4Z" {...bodyFill} />
    <path d="M6 78 h84" {...ground} />
  </svg>
);

// --- PLANK (forearm plank, side view) -------------------------------------
const Plank = () => (
  <svg viewBox="0 0 100 100" filter="url(#rough)">
    {roughFilter}
    {/* head */}
    <path d="M14 40 q7 -1 8 6 q0 6 -8 6 q-6 -1 -6 -6 q0 -6 6 -6Z" {...bodyFill} />
    {/* long body */}
    <path d="M22 46 q28 2 52 12 q3 1 3 3 q0 2 -3 3 q-28 -2 -52 -6Z" {...bodyFill} />
    {/* forearm on ground */}
    <path d="M18 52 q0 12 -2 20 q0 2 3 3 q3 0 4 -3 q1 -8 3 -18Z" {...bodyFill} />
    {/* rear legs */}
    <path d="M72 58 q10 6 18 12 q2 2 -1 3 q-6 1 -12 -3 q-8 -4 -10 -8Z" {...bodyFill} />
    <path d="M6 78 h84" {...ground} />
  </svg>
);

// --- LUNGE (deep lunge, side view) ----------------------------------------
const Lunge = () => (
  <svg viewBox="0 0 100 100" filter="url(#rough)">
    {roughFilter}
    {/* head */}
    <path d="M44 8 q7 0 8 7 q0 7 -8 8 q-7 -1 -7 -8 q0 -7 7 -7Z" {...bodyFill} />
    {/* torso upright */}
    <path d="M40 22 q-2 10 0 22 q0 6 6 8 q10 0 12 -6 q3 -10 0 -22 q-4 -6 -18 -2Z" {...bodyFill} />
    {/* front arm on hip */}
    <path d="M42 30 q-8 4 -8 14 q1 3 3 3 q3 0 4 -3 q2 -6 4 -10Z" {...bodyFill} />
    {/* front thigh going down */}
    <path d="M42 46 q-6 12 -14 22 q-1 3 2 4 q3 1 5 -1 q10 -10 14 -20Z" {...bodyFill} />
    {/* front shin vertical */}
    <path d="M26 70 q0 8 2 14 q1 2 4 2 q3 0 3 -3 q-1 -8 -1 -13Z" {...bodyFill} />
    {/* back leg extended */}
    <path d="M56 50 q16 6 26 14 q3 2 0 4 q-5 3 -10 1 q-14 -6 -22 -12Z" {...bodyFill} />
    {/* back foot */}
    <path d="M78 66 q6 2 10 6 q1 2 -1 3 q-5 1 -11 -4Z" {...bodyFill} />
    <path d="M20 88 q6 -2 12 -2 h6" {...ground} />
    <path d="M60 88 h30" {...ground} />
  </svg>
);

// --- GLUTE BRIDGE (supine hips up) ----------------------------------------
const Bridge = () => (
  <svg viewBox="0 0 100 100" filter="url(#rough)">
    {roughFilter}
    {/* head on ground */}
    <path d="M12 62 q7 -1 8 6 q0 6 -8 6 q-6 -1 -6 -6 q0 -6 6 -6Z" {...bodyFill} />
    {/* raised body: shoulders on ground, hips lifted */}
    <path d="M20 66 q10 -4 26 -18 q10 -8 22 4 q6 6 12 12 q2 3 -2 5 q-6 2 -12 -2 q-14 -8 -22 -2 q-10 6 -24 6Z" {...bodyFill} />
    {/* thigh going down to feet */}
    <path d="M64 60 q6 8 10 20 q0 2 -3 2 q-3 0 -4 -2 q-6 -8 -10 -16Z" {...bodyFill} />
    <path d="M6 84 h84" {...ground} />
  </svg>
);

// --- BICEP CURL (standing, dumbbells at hips) -----------------------------
const BicepCurl = () => (
  <svg viewBox="0 0 100 100" filter="url(#rough)">
    {roughFilter}
    {/* head */}
    <path d="M50 8 q7 0 8 7 q0 7 -8 8 q-7 -1 -7 -8 q0 -7 7 -7Z" {...bodyFill} />
    {/* torso */}
    <path d="M42 22 q-2 12 0 24 q0 6 8 8 q10 0 12 -6 q2 -12 0 -26 q-4 -4 -20 0Z" {...bodyFill} />
    {/* right arm bent, curl up */}
    <path d="M62 30 q6 4 6 14 q0 4 -3 5 q-4 0 -5 -3 q-2 -8 -2 -14Z" {...bodyFill} />
    {/* left arm at side */}
    <path d="M40 30 q-4 8 -4 20 q0 3 3 3 q3 0 4 -3 q0 -10 2 -18Z" {...bodyFill} />
    {/* dumbbells */}
    <rect x="60" y="46" width="12" height="6" rx="2" {...equipment} />
    <rect x="32" y="52" width="12" height="6" rx="2" {...equipment} />
    {/* legs */}
    <path d="M46 50 q-3 15 -4 32 q0 2 3 2 q3 0 4 -2 q1 -14 3 -30Z" {...bodyFill} />
    <path d="M54 50 q3 15 4 32 q0 2 -3 2 q-3 0 -4 -2 q-1 -14 -3 -30Z" {...bodyFill} />
    <path d="M8 86 h84" {...ground} />
  </svg>
);

// --- OVERHEAD PRESS (arms up with bar) ------------------------------------
const OverheadPress = () => (
  <svg viewBox="0 0 100 100" filter="url(#rough)">
    {roughFilter}
    {/* head */}
    <path d="M50 28 q7 0 8 7 q0 7 -8 8 q-7 -1 -7 -8 q0 -7 7 -7Z" {...bodyFill} />
    {/* torso */}
    <path d="M42 42 q-2 10 0 20 q0 6 8 8 q10 0 12 -6 q2 -10 0 -22 q-4 -4 -20 0Z" {...bodyFill} />
    {/* arms up on either side of head */}
    <path d="M42 42 q-4 -12 -6 -30 q0 -3 3 -3 q3 0 4 3 q4 12 4 26Z" {...bodyFill} />
    <path d="M62 42 q4 -12 6 -30 q0 -3 -3 -3 q-3 0 -4 3 q-4 12 -4 26Z" {...bodyFill} />
    {/* barbell across the top */}
    <rect x="24" y="8" width="52" height="5" rx="2" {...equipment} />
    <rect x="20" y="4" width="6" height="13" rx="1.5" {...equipment} />
    <rect x="74" y="4" width="6" height="13" rx="1.5" {...equipment} />
    {/* legs */}
    <path d="M46 66 q-2 12 -3 22 q0 2 3 2 q3 0 3 -2 q0 -10 2 -20Z" {...bodyFill} />
    <path d="M54 66 q2 12 3 22 q0 2 -3 2 q-3 0 -3 -2 q0 -10 -2 -20Z" {...bodyFill} />
    <path d="M8 90 h84" {...ground} />
  </svg>
);

// --- BENT-OVER ROW (side view, torso forward, weight pulled to hip) -------
const Row = () => (
  <svg viewBox="0 0 100 100" filter="url(#rough)">
    {roughFilter}
    {/* head forward */}
    <path d="M22 32 q7 -1 8 6 q0 6 -8 6 q-6 -1 -6 -6 q0 -6 6 -6Z" {...bodyFill} />
    {/* torso hinged forward */}
    <path d="M28 36 q28 2 46 12 q3 1 3 4 q-1 3 -4 3 q-24 0 -46 -6Z" {...bodyFill} />
    {/* arm pulling elbow back */}
    <path d="M62 46 q10 -4 20 -2 q3 1 3 3 q-1 3 -3 3 q-10 2 -18 4Z" {...bodyFill} />
    {/* dumbbell */}
    <rect x="76" y="42" width="12" height="6" rx="2" {...equipment} />
    {/* legs slightly bent */}
    <path d="M44 46 q-2 14 -6 32 q0 2 3 3 q3 0 4 -2 q4 -14 8 -30Z" {...bodyFill} />
    <path d="M54 46 q4 14 8 32 q1 2 -2 3 q-3 0 -4 -2 q-4 -14 -8 -30Z" {...bodyFill} />
    <path d="M6 82 h84" {...ground} />
  </svg>
);

// --- CARDIO (running silhouette) ------------------------------------------
const Cardio = () => (
  <svg viewBox="0 0 100 100" filter="url(#rough)">
    {roughFilter}
    {/* head */}
    <path d="M52 8 q7 0 8 7 q0 7 -8 8 q-7 -1 -7 -8 q0 -7 7 -7Z" {...bodyFill} />
    {/* torso leaning forward */}
    <path d="M46 22 q-3 8 -4 20 q0 5 6 6 q10 0 12 -4 q3 -8 2 -20 q-4 -4 -16 -2Z" {...bodyFill} />
    {/* front arm pumping up */}
    <path d="M60 26 q10 -4 14 -14 q1 -3 -2 -4 q-3 -1 -5 2 q-6 6 -12 12Z" {...bodyFill} />
    {/* back arm swinging behind */}
    <path d="M42 32 q-14 4 -22 12 q-2 2 0 4 q3 2 6 0 q10 -4 18 -10Z" {...bodyFill} />
    {/* front leg raised knee up */}
    <path d="M52 46 q-8 4 -12 16 q-1 4 2 5 q4 0 6 -3 q6 -10 10 -14Z" {...bodyFill} />
    {/* back leg extended */}
    <path d="M54 48 q10 8 20 20 q2 2 -1 3 q-4 1 -8 -2 q-10 -6 -18 -14Z" {...bodyFill} />
    <path d="M8 84 h84" {...ground} />
  </svg>
);

// --- SEATED SPINAL TWIST --------------------------------------------------
const SpineTwist = () => (
  <svg viewBox="0 0 100 100" filter="url(#rough)">
    {roughFilter}
    {/* legs crossed / seated */}
    <path d="M20 62 q30 -2 60 4 q4 1 4 6 q-1 6 -6 6 q-30 -2 -58 -6Z" {...bodyFill} />
    {/* seated torso rotated */}
    <path d="M42 32 q-6 12 -4 26 q0 4 6 4 q12 0 14 -6 q2 -14 -2 -26 q-4 -4 -14 2Z" {...bodyFill} />
    {/* head looking back */}
    <path d="M48 14 q8 -2 10 6 q0 8 -8 10 q-8 0 -8 -8 q0 -8 6 -8Z" {...bodyFill} />
    {/* arm reaching across */}
    <path d="M42 34 q-14 8 -20 22 q-1 3 2 4 q3 0 5 -2 q10 -10 16 -18Z" {...bodyFill} />
    {/* twist arrow */}
    <path d="M62 20 q10 6 12 18" stroke={ACCENT} strokeWidth="1.8" fill="none" strokeLinecap="round" />
    <path d="M74 38 l-4 -6 M74 38 l-6 2" stroke={ACCENT} strokeWidth="1.8" fill="none" strokeLinecap="round" />
    <path d="M8 82 h84" {...ground} />
  </svg>
);

// --- FORWARD FOLD (bending over, touching toes) --------------------------
const ForwardFold = () => (
  <svg viewBox="0 0 100 100" filter="url(#rough)">
    {roughFilter}
    {/* legs standing */}
    <path d="M44 44 q-2 20 -2 38 q0 2 3 2 q3 0 3 -2 q0 -18 2 -36Z" {...bodyFill} />
    <path d="M54 44 q2 20 2 38 q0 2 -3 2 q-3 0 -3 -2 q0 -18 -2 -36Z" {...bodyFill} />
    {/* torso folded forward */}
    <path d="M44 40 q0 -10 12 -14 q10 -2 12 6 q0 8 -6 12 q-14 4 -18 -4Z" {...bodyFill} />
    {/* head hanging down */}
    <path d="M42 46 q-8 4 -8 12 q1 4 4 4 q4 0 6 -4 q3 -5 3 -10Z" {...bodyFill} />
    <path d="M44 24 q6 -2 12 0" {...detailLine} />
    {/* arms hanging to toes */}
    <path d="M50 42 q-4 16 -8 32 q0 3 3 3 q3 0 4 -3 q4 -16 6 -28Z" {...bodyFill} />
    <path d="M8 84 h84" {...ground} />
  </svg>
);

// --- HAMSTRING STRETCH (seated, one leg extended, reaching forward) -------
const HamstringStretch = () => (
  <svg viewBox="0 0 100 100" filter="url(#rough)">
    {roughFilter}
    {/* extended leg */}
    <path d="M24 62 q30 0 58 -6 q4 -1 4 4 q0 4 -4 5 q-30 6 -58 6Z" {...bodyFill} />
    {/* torso reaching forward */}
    <path d="M22 42 q-4 14 0 22 q4 6 12 2 q14 -10 16 -22 q0 -6 -6 -8 q-12 -2 -22 6Z" {...bodyFill} />
    {/* head */}
    <path d="M22 30 q8 -2 10 6 q0 8 -8 10 q-8 0 -8 -8 q0 -8 6 -8Z" {...bodyFill} />
    {/* arms reaching toes */}
    <path d="M40 44 q14 -2 26 -8 q3 -1 3 2 q0 3 -3 4 q-14 6 -24 8Z" {...bodyFill} />
    <path d="M8 84 h84" {...ground} />
  </svg>
);

// --- NECK STRETCH (head tilted to side) -----------------------------------
const NeckStretch = () => (
  <svg viewBox="0 0 100 100" filter="url(#rough)">
    {roughFilter}
    {/* head tilted */}
    <path d="M38 18 q0 -12 14 -12 q14 0 14 12 q0 12 -14 14 q-14 0 -14 -14Z" {...bodyFill} />
    {/* neck line */}
    <path d="M48 30 q3 4 8 6" {...detailLine} />
    {/* torso */}
    <path d="M42 34 q-4 14 0 26 q4 6 12 6 q10 -2 12 -8 q4 -14 0 -24 q-4 -4 -24 0Z" {...bodyFill} />
    {/* arm reaching over head */}
    <path d="M50 18 q10 -2 22 -2 q3 0 3 3 q0 3 -3 3 q-12 2 -22 2Z" {...bodyFill} />
    {/* arm at side */}
    <path d="M42 40 q-6 8 -6 20 q0 3 3 3 q3 0 4 -3 q0 -10 2 -18Z" {...bodyFill} />
    {/* legs */}
    <path d="M46 62 q-2 12 -2 22 q0 2 3 2 q3 0 3 -2 q0 -10 2 -20Z" {...bodyFill} />
    <path d="M54 62 q2 12 2 22 q0 2 -3 2 q-3 0 -3 -2 q0 -10 -2 -20Z" {...bodyFill} />
    <path d="M8 88 h84" {...ground} />
  </svg>
);

// --- CHILD'S POSE (kneeling forward fold) ---------------------------------
const ChildPose = () => (
  <svg viewBox="0 0 100 100" filter="url(#rough)">
    {roughFilter}
    {/* rounded back curve, hips high */}
    <path d="M20 68 q10 -22 40 -22 q22 0 30 20 q1 3 -3 4 q-30 6 -66 4 q-3 0 -1 -6Z" {...bodyFill} />
    {/* head resting forward */}
    <path d="M18 66 q8 -2 10 6 q0 8 -8 10 q-8 0 -8 -8 q0 -8 6 -8Z" {...bodyFill} />
    {/* arms extended forward */}
    <path d="M22 60 q10 -4 26 -8 q4 -1 4 2 q0 3 -3 4 q-14 4 -24 6Z" {...bodyFill} />
    <path d="M6 82 h84" {...ground} />
  </svg>
);

// --- Generic workout (standing athlete, hands on hips) -------------------
const GenericWorkout = () => (
  <svg viewBox="0 0 100 100" filter="url(#rough)">
    {roughFilter}
    <path d="M50 8 q7 0 8 7 q0 7 -8 8 q-7 -1 -7 -8 q0 -7 7 -7Z" {...bodyFill} />
    <path d="M42 22 q-2 14 0 28 q0 6 8 8 q10 0 12 -6 q2 -14 0 -30 q-4 -4 -20 0Z" {...bodyFill} />
    {/* both arms elbows out (akimbo) */}
    <path d="M42 30 q-10 4 -12 12 q0 3 3 3 q3 0 4 -3 q3 -5 6 -8Z" {...bodyFill} />
    <path d="M62 30 q10 4 12 12 q0 3 -3 3 q-3 0 -4 -3 q-3 -5 -6 -8Z" {...bodyFill} />
    {/* legs */}
    <path d="M46 56 q-3 14 -3 26 q0 2 3 2 q3 0 3 -2 q0 -12 2 -22Z" {...bodyFill} />
    <path d="M54 56 q3 14 3 26 q0 2 -3 2 q-3 0 -3 -2 q0 -12 -2 -22Z" {...bodyFill} />
    <path d="M8 86 h84" {...ground} />
  </svg>
);

// --- Generic stretch (person in a gentle side stretch) --------------------
const GenericStretch = () => (
  <svg viewBox="0 0 100 100" filter="url(#rough)">
    {roughFilter}
    <path d="M52 8 q7 0 8 7 q0 7 -8 8 q-7 -1 -7 -8 q0 -7 7 -7Z" {...bodyFill} />
    {/* body curved sideways */}
    <path d="M46 22 q-10 12 -10 28 q0 6 6 8 q10 0 14 -4 q6 -14 4 -30 q-4 -4 -14 -2Z" {...bodyFill} />
    {/* arm reaching up and over */}
    <path d="M50 22 q10 -8 22 -6 q3 1 2 4 q-1 3 -4 3 q-10 0 -18 4Z" {...bodyFill} />
    <path d="M46 54 q-3 14 -3 28 q0 2 3 2 q3 0 3 -2 q0 -12 2 -24Z" {...bodyFill} />
    <path d="M56 54 q3 14 3 28 q0 2 -3 2 q-3 0 -3 -2 q0 -12 -2 -24Z" {...bodyFill} />
    <path d="M8 86 h84" {...ground} />
  </svg>
);

// --- Matcher --------------------------------------------------------------

export const RULES = [
  [/squat|goblet/i, Squat],
  [/push[- ]?up|press[- ]?up|chaturanga/i, Pushup],
  [/plank|hollow hold|core hold|dead bug|bird dog/i, Plank],
  [/lunge|split squat|step[- ]?up/i, Lunge],
  [/glute bridge|hip bridge|bridge|hip thrust/i, Bridge],
  [/curl|bicep|hammer/i, BicepCurl],
  [/overhead|shoulder press|press$|military|leg press/i, OverheadPress],
  [/row|pull[- ]?down|pull[- ]?up|chin[- ]?up|deadlift|face pull|farmer/i, Row],
  [/run|jog|walk|cardio|hiit|burpee|jumping jack|skip|jump/i, Cardio],
  [/twist|rotation|spinal|thoracic|wood chop|cat[- ]?cow/i, SpineTwist],
  [/forward fold|toe touch|standing fold/i, ForwardFold],
  [/hamstring|seated stretch|pike|figure[- ]?4/i, HamstringStretch],
  [/neck|upper trap|scalene/i, NeckStretch],
  [/child.?pose|childs pose|pigeon|bow pose|cobra|downward dog|yoga|hip flexor/i, ChildPose],
  [/stretch|mobility|flexibility|foam roll|open|release/i, GenericStretch],
];

export const pickSketch = (name = '') => {
  for (const [re, C] of RULES) if (re.test(name)) return C;
  return GenericWorkout;
};

const ExerciseSketch = ({ name, tone = 'workout', size = 72, onClick }) => {
  const Sketch = pickSketch(name || '');
  const bg = tone === 'stretch' ? 'bg-[#EEF2EA]' : 'bg-[#F2EFE9]';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`shrink-0 rounded-2xl ${bg} border border-[#E5E1D8] p-1.5 flex items-center justify-center transition-all ${onClick ? 'hover:border-[#7E9979] hover:shadow-sm active:scale-95 cursor-pointer' : 'cursor-default'}`}
      style={{ width: size, height: size }}
      data-testid={`exercise-sketch-${(name || 'x').toLowerCase().replace(/\s+/g, '-').slice(0, 30)}`}
      aria-label={onClick ? `Show ${name} form loop` : undefined}
    >
      <Sketch />
    </button>
  );
};

export default ExerciseSketch;
